<template>
  <div class="product-imei-revenue-report-page">
    <KTCodePreview v-bind:title="'Báo cáo lãi lỗ theo sản phẩm IMEI'">
      <template v-slot:preview>
        <div>
          <ProductImeiRevenueReportFilter
            :initial-filter="initialFilterData"
            :stores="stores"
            :options="billTypeOptions"
            :loading="onLoading"
            :has-export-permission="checkPermission('PRODUCT_IMEI_REVENUE_REPORT_EXPORT')"
            :category-tree-options="categoryTreeOptions"
            :product-suggestions="productSearchSuggestions"
            @filter="handleFilter"
            @product-search="onProductSearchInput"
            @refresh="handleRefresh"
            @export="handleExport"
          />
          <ProductImeiRevenueReportTable
            class="mt-3"
            :items="reportData"
            :loading="onLoading"
            :total-count="totalCount"
            :current-page="apiParams.page"
            :per-page="apiParams.limit"
            :summary="summaryData"
            :last-sync-at="lastSyncAt"
            :view-mode="viewMode"
            @page-change="handlePageChange"
            @view-mode-change="handleViewModeChange"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import moment from 'moment'
import debounce from 'debounce'
import { makeToastFaile } from '@/utils/common'

import ProductImeiRevenueReportFilter from './components/ProductImeiRevenueReportFilter.vue'
import ProductImeiRevenueReportTable from './components/ProductImeiRevenueReportTable.vue'
import ProductImeiRevenueReportService from './services/ProductImeiRevenueReportService.js'
import { BILL_TYPE_NAME } from '@/utils/enum-name'
import { showLoadingDialog, closeSwal } from '@/utils/sweet-alert2'
import permissionMixin from '@/view/mixins/permission.mixin';

export default {
  mixins: [permissionMixin],
  data() {
    return {
      onLoading: false,
      stores: [],
      billTypeOptions: [],
      initialFilterData: {},
      categoryTreeOptions: [],
      productSearchSuggestions: [],
      apiParams: {
        page: 1,
        limit: 10,
        startDate: null,
        endDate: null,
        storeId: null,
        billType: null,
        imeiCode: '',
        billNumber: '',
        categoryIds: [],
        productId: null,
        groupByImei: true,
      },
      viewMode: 'imei',
      reportData: [],
      summaryData: null,
      lastSyncAt: null,
      totalCount: 0,
    }
  },
  components: {
    KTCodePreview,
    ProductImeiRevenueReportFilter,
    ProductImeiRevenueReportTable,
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Báo cáo lãi lỗ', route: '/reports/profit-loss' },
      { title: 'Theo sản phẩm IMEI' },
    ])
  },
  created() {
    this.debouncedProductSearch = debounce((text) => {
      ProductImeiRevenueReportService.searchProductsByName(text).then((rows) => {
        this.productSearchSuggestions = rows
      })
    }, 500)
    this.initialize()
  },
  methods: {
    onProductSearchInput(text) {
      const t = text != null ? String(text) : ''
      if (!t.trim()) {
        this.productSearchSuggestions = []
        return
      }
      this.debouncedProductSearch(t)
    },

    async initialize() {
      const today = moment()
      const startDate = today.clone().subtract(29, 'days').format('DD/MM/YYYY')
      const endDate = today.format('DD/MM/YYYY')

      this.billTypeOptions = [
        { value: null, text: 'Tất cả loại hoá đơn' },
        ...Object.entries(BILL_TYPE_NAME).map(([value, text]) => ({
          value: Number(value),
          text,
        })),
      ]

      this.initialFilterData = {
        startDate,
        endDate,
        storeId: null,
        billType: null,
        imeiCode: '',
        billNumber: '',
        categoryIds: [],
        productId: null,
      }

      this.apiParams = {
        ...this.apiParams,
        page: 1,
        startDate,
        endDate,
        categoryIds: [],
        productId: null,
        groupByImei: this.viewMode === 'imei',
      }

      try {
        this.stores = await ProductImeiRevenueReportService.fetchStoresByUser()
        this.categoryTreeOptions = await ProductImeiRevenueReportService.fetchCategoryTreeOptions()
        await this.fetchSyncState()
        await this.fetchReportData()
      } catch (error) {
        this.onLoading = false
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      }
    },

    async fetchSyncState() {
      try {
        const syncState = await ProductImeiRevenueReportService.fetchSyncState()
        this.lastSyncAt = syncState?.lastSyncAt || null
      } catch {
        this.lastSyncAt = null
      }
    },

    async fetchReportData(options = {}) {
      const { skipTableLoading = false } = options
      if (!skipTableLoading) this.onLoading = true
      try {
        const [listData, summaryData] = await Promise.all([
          ProductImeiRevenueReportService.fetchReportList(this.apiParams),
          ProductImeiRevenueReportService.fetchReportSummary(this.apiParams),
        ])
        this.reportData = listData?.dataset || []
        this.totalCount = listData?.totalRow || 0
        this.summaryData = summaryData || null
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      } finally {
        if (!skipTableLoading) this.onLoading = false
      }
    },

    handleFilter(filterData) {
      this.apiParams = {
        ...this.apiParams,
        page: 1,
        startDate: filterData.startDate,
        endDate: filterData.endDate,
        storeId: filterData.storeId,
        billType: filterData.billType,
        imeiCode: filterData.imeiCode,
        billNumber: filterData.billNumber,
        categoryIds: filterData.categoryIds || [],
        productId: filterData.productId,
        groupByImei: this.viewMode === 'imei',
      }
      this.fetchReportData()
    },

    handleViewModeChange(mode) {
      if (this.viewMode === mode) return
      this.viewMode = mode
      this.apiParams = {
        ...this.apiParams,
        page: 1,
        groupByImei: mode === 'imei',
      }
      this.fetchReportData()
    },

    handlePageChange(page) {
      this.apiParams = { ...this.apiParams, page }
      this.fetchReportData()
    },

    async handleRefresh() {
      showLoadingDialog('Đang đồng bộ dữ liệu...', 'Vui lòng chờ...')
      try {
        await ProductImeiRevenueReportService.syncData()
        await this.fetchSyncState()
        await this.fetchReportData({ skipTableLoading: true })
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      } finally {
        closeSwal()
      }
    },

    async handleExport() {
      try {
        await ProductImeiRevenueReportService.exportExcel(this.apiParams)
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      }
    },
  },
}
</script>

<style scope>
.d-flex {
  display: -webkit-box !important;
}
</style>

<style lang="scss" scoped>
.product-imei-revenue-report-page {
  table {
    margin: 0;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
  }
}
</style>
